import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { apiFetchUnchecked } from "../../api/client.js";
import { getArticlesForRoute, searchArticles, type HelpArticle } from "../../help/registry.js";
import { HelpMarkdown } from "../help/markdown.js";
import { Button } from "../ui/button.js";
import { Input } from "../ui/input.js";

import type { PageGuide } from "../../help/page-guide.js";

interface HelpPanelProps {
  open: boolean;
  onClose: () => void;
  currentRoute: string;
  /** Page guide for the current route, if any (drives the "This page" tab). */
  guide?: PageGuide | null;
  /** Replays the first-run walkthrough for the current page. */
  onReplayTour?: () => void;
}

type HelpTab = "guide" | "articles" | "ask";

function scrollToAnchor(anchorId: string) {
  const el = document.querySelector(`[data-help-anchor="${anchorId}"]`);
  el?.scrollIntoView({ behavior: "smooth", block: "center" });
}

export function HelpPanel({ open, onClose, currentRoute, guide, onReplayTour }: HelpPanelProps) {
  const [search, setSearch] = useState("");
  const [aiQuestion, setAiQuestion] = useState("");
  const [activeTab, setActiveTab] = useState<HelpTab>(guide ? "guide" : "articles");

  const contextArticles = getArticlesForRoute(currentRoute);
  const searchResults = search ? searchArticles(search) : contextArticles;

  const askMutation = useMutation({
    mutationFn: (question: string) =>
      apiFetchUnchecked<{
        answer: string;
        relatedArticles: Array<{ title: string; path: string }>;
      }>("/help/ask", {
        method: "POST",
        body: JSON.stringify({ question, currentRoute }),
      }),
  });

  function handleAsk(e: React.FormEvent) {
    e.preventDefault();
    if (!aiQuestion.trim()) return;
    askMutation.mutate(aiQuestion);
  }

  if (!open) return null;

  const tab = activeTab === "guide" && !guide ? "articles" : activeTab;

  return (
    <div className="fixed right-0 top-0 z-50 flex h-full w-96 flex-col border-l bg-surface shadow-xl">
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-3">
        <h2 className="font-semibold text-fg">Help</h2>
        <button onClick={onClose} className="text-fg-subtle hover:text-fg-muted">
          &times;
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b">
        {guide && (
          <TabButton active={tab === "guide"} onClick={() => setActiveTab("guide")}>
            This page
          </TabButton>
        )}
        <TabButton active={tab === "articles"} onClick={() => setActiveTab("articles")}>
          Articles
        </TabButton>
        <TabButton active={tab === "ask"} onClick={() => setActiveTab("ask")}>
          Ask / Do
        </TabButton>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {tab === "guide" && guide && (
          <div className="space-y-5 p-4">
            <div>
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-fg">Workflow</h3>
                {onReplayTour && (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      onClose();
                      onReplayTour();
                    }}
                  >
                    Replay tour
                  </Button>
                )}
              </div>
              <ol className="mt-2 space-y-2">
                {guide.steps.map((step, i) => (
                  <li key={step.id} className="rounded-md border bg-surface-muted p-3">
                    <p className="text-sm font-medium text-fg">
                      {i + 1}. {step.title}
                    </p>
                    <HelpMarkdown
                      content={step.body}
                      className="mt-1 space-y-1 text-xs text-fg-muted"
                    />
                  </li>
                ))}
              </ol>
            </div>
            <div className={guide.panels.length === 0 ? "hidden" : undefined}>
              <h3 className="text-sm font-semibold text-fg">On this page</h3>
              <div className="mt-2 space-y-2">
                {guide.panels.map((panel) => (
                  <div key={panel.anchorId} className="rounded-md border p-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-fg">{panel.title}</p>
                      <button
                        type="button"
                        onClick={() => {
                          onClose();
                          scrollToAnchor(panel.anchorId);
                        }}
                        className="shrink-0 text-xs font-medium text-fg-brand hover:underline"
                      >
                        Jump to
                      </button>
                    </div>
                    <HelpMarkdown
                      content={panel.body}
                      className="mt-1 space-y-1 text-xs text-fg-muted"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "articles" && (
          <div className="p-4">
            <Input
              placeholder="Search help..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="mt-4 space-y-3">
              {searchResults.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
              {searchResults.length === 0 && (
                <p className="text-center text-sm text-fg-subtle">No articles found.</p>
              )}
            </div>
          </div>
        )}

        {tab === "ask" && (
          <div className="p-4">
            <div className="mb-4 space-y-2 rounded-md border bg-surface-muted p-3 text-xs text-fg-muted">
              <p>
                Explain uses the help articles on this page. Guide me opens the screen and
                highlights a control. It does not change data.
              </p>
              {contextArticles.flatMap((article) =>
                (article.guideTargets ?? []).map((target) => (
                  <a
                    key={`${article.id}-${target.controlId ?? target.route}`}
                    href={target.route}
                    className="block font-medium text-fg-brand hover:underline"
                  >
                    Guide me: {target.intent}
                  </a>
                )),
              )}
              <p>
                To inspect readiness or publish after a confirmation card, open{" "}
                <a href="/ai" className="font-medium text-fg-brand hover:underline">
                  Ask Insights
                </a>
                . Publishing stays off unless portal actions are enabled, and every write still
                needs confirmation.
              </p>
            </div>
            <form onSubmit={handleAsk} className="space-y-3">
              <textarea
                value={aiQuestion}
                onChange={(e) => setAiQuestion(e.target.value)}
                placeholder="Ask a question about L30 Insights..."
                rows={3}
                className="block w-full rounded-md border border-strong px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
              <Button type="submit" disabled={askMutation.isPending || !aiQuestion.trim()}>
                {askMutation.isPending ? "Thinking..." : "Ask"}
              </Button>
            </form>

            {askMutation.data && (
              <div className="mt-4">
                <div className="rounded-lg bg-info-bg p-3 text-sm text-fg">
                  {askMutation.data.answer}
                </div>
                {askMutation.data.relatedArticles.length > 0 && (
                  <div className="mt-3">
                    <h4 className="text-xs font-semibold uppercase text-fg-subtle">Related</h4>
                    <div className="mt-1 space-y-1">
                      {askMutation.data.relatedArticles.map((a) => (
                        <a
                          key={a.path}
                          href={a.path}
                          className="block text-sm text-fg-brand hover:underline"
                        >
                          {a.title}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {askMutation.error && (
              <p className="mt-3 text-sm text-danger-solid">
                Failed to get answer. Please try again.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 px-4 py-2 text-sm font-medium ${
        active ? "border-b-2 border-brand text-fg-brand" : "text-fg-subtle hover:text-fg"
      }`}
    >
      {children}
    </button>
  );
}

function ArticleCard({ article }: { article: HelpArticle }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-lg border p-3">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center justify-between text-left"
      >
        <div>
          <div className="text-sm font-medium text-fg">{article.title}</div>
          <div className="text-xs text-fg-subtle">{article.category}</div>
        </div>
        <span className="text-fg-subtle">{expanded ? "−" : "+"}</span>
      </button>
      {expanded && (
        <HelpMarkdown content={article.content} className="mt-3 space-y-1 text-sm text-fg-muted" />
      )}
    </div>
  );
}
